import React, { useEffect, CSSProperties } from "react";
import { Box, Tabs, Tab, Button, Paper, Typography, Link } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import DocNavigation from "../components/DocNavigation";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { CopyBlock, dracula } from "react-code-blocks";
import BackendResponseCodeMapping from './BackendResponseCodeMapping';
import MultiConsent from './MultiConsent';
import CoCreatedGuideLines from './CoCreatedGuideLines';

const styles: { [key: string]: CSSProperties } = {
  container: {
    fontFamily: "Arial, sans-serif",
    padding: "20px",
    backgroundColor: "#f9f9f9",
    color: "#333",
  },
  section: {
    marginBottom: "20px",
  },
  codeBlock: {
    backgroundColor: "#eee",
    padding: "10px",
    borderRadius: "4px",
    fontFamily: "monospace",
    overflowX: "auto",
    whiteSpace: "pre-wrap",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: "20px",
  },
  th: {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "left",
    backgroundColor: "#f2f2f2",
  },
  td: {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "left",
  },
};

const WebSDKIntegration: React.FC = React.memo(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [selectedPlatform, setSelectedPlatform] =
    React.useState("react-native");

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [location.pathname]);

  useEffect(() => {
    if (location.hash === "#event-tracking") {
      setSelectedTab(1);
    } else if (location.hash === "#multiconsent") {
      setSelectedTab(2);
    } else if (location.hash === "#response-code-mapping") {
      setSelectedTab(3);
    } else {
      setSelectedTab(0);
    }
    console.log("Current hash:", location.hash);
  }, [location.hash]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    const hashMap = ['#sdk', '#event-tracking', '#multiconsent', '#response-code-mapping'];
    navigate(`/web-sdk-integration${hashMap[newValue]}`);
  };

  const handlePlatformChange = (platform: string) => {
    setSelectedPlatform(platform);
  };

  const SDKContent = () => (
    <Box sx={styles.container}>
      <section style={styles.section}>
        <CoCreatedGuideLines />
        <section style={styles.section}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 2, 
            border: "2px solid #0b3452",
            mb: 3
          }}>
            <Typography variant="h6" sx={{ mt: 0, color: "#0b3452", fontWeight: "bold" }}>
              Latest SDK Version
            </Typography>
            <Typography sx={{ 
              mt: 2, 
              color: "#0b3452", 
              fontSize: "1.2rem",
              fontWeight: "bold"
            }}>
              Version: 2.7.1
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{ mt: 0, color: "#0b3452", fontWeight: "bold" }}
          >
            Overview
          </Typography>
          <p>
            The Finvu WebSocket wrapper library enables seamless interaction
            between FIUs and Finvu AA. It can be embedded in any web
            application.
          </p>

          <p>CDN Paths:</p>
          <ul>
            <li>
              <strong>UAT:</strong>{" "}
              <code>
                https://reactjssdk.finvu.in/sdk/connector/finvu-client-sdk.js
              </code>
            </li>
            <li>
              <strong>Production:</strong>{" "}
              <code>
                https://webvwlive.finvu.in/sdk/connector/finvu-client-sdk.js
              </code>
            </li>
          </ul>
          <CopyBlock
            text={`// 1. Include the SDK
UAT : <script src="https://reactjssdk.finvu.in/sdk/connector/finvu-client-sdk.js"></script>
Production : <script src="https://webvwlive.finvu.in/sdk/connector/finvu-client-sdk.js"></script>`}
            language="html"
            showLineNumbers={true}
            theme={dracula}
          />
        </section>

		<Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong> finvuClient.openV2()
            </Typography>
            <Typography
              sx={{
                fontFamily: "monospace",
                color: "#666",
                mt: 1,
                fontSize: "0.9em",
              }}
            >
                The openV2() method is used to initialize the WebSocket connection and it returns csid (session id) which is used to identify the session and is used in all the subsequent calls.
                <br /><br />
                Note: If you want to disable the logs printed by the SDK, then call the openV2 method like this openV2('', '', {'{'}enableLog:false{'}'});
            </Typography>
          </Box>

        <section style={styles.section}>
          <Typography
            variant="h6"
            sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}
          >
            API Documentation
          </Typography>

          {/* 1. Decrypting Request Parameters */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            1. Decrypting Request Parameters
          </Typography>
          <p>
            Sets up the encrypted request context required for authentication.
          </p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong> finvuClient.fiuInfo(ecreq, timestamp,
              fiuId, instType)
            </Typography>
            <Typography
              sx={{
                fontFamily: "monospace",
                color: "#666",
                mt: 1,
                fontSize: "0.9em",
                whiteSpace: "pre-line",
              }}
            >
              {`Parameters: 
- ecreq : Encrypted request string
- timestamp : Encrypted request date
- fiuId : Financial institution ID
- instType : Optional institution type`}
            </Typography>
             <Typography
               sx={{
                 fontFamily: "monospace",
                 color: "#666",
                 mt: 1,
                 fontSize: "0.9em",
               }}
             >
               Note: These parameters are received from your backend API when initiating a consent request.
               <br /><br />
               Important: In all responses, there will be a field named 'code' to know the status of the response. 
               To know more, please refer to the{' '}
               <Link 
                 href="#response-code-mapping" 
                 onClick={() => navigate('/web-sdk-integration#response-code-mapping')}
                 sx={{ color: "#0b3452", textDecoration: "underline", cursor: "pointer" }}
               >
                 Response Code Mapping
               </Link>
               {' '}section.
             </Typography>
          </Box>

          <h4>Request Example</h4>
          <CopyBlock
            text={`finvuClient.fiuInfo(
    encryptedRequest,
    timestamp,
    fiuId,
    instType
)`}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />

		  <h4>Request Schema</h4>
		          <CopyBlock
            text={`{
    "header": {
        "mid": "String",
        "ts": "String",
        "dup": "Boolean",
        "sid": "", 
        "type": "String",
	    "csid": "String" 
    },
    "payload": {
        "encryptedRequest": "String", // Encrypted request string
        "requestDate": "String", // Encrypted request date
        "encryptedFiuId": "String", // Encrypted financial institution ID
        "encryptedFiType": null
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />
          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",
        "ts": "String",
        "dup": "Boolean",
        "sid": "", 
        "type": "String",
        "csid": "String"
    },
    "payload": {
        "status": "String", // e.g., "SUCCESS" or "FAILURE"
        "code": "String", // e.g., "F200" 
        "message": "String",
        "userIdOrMobileNo": "MobileNumber@finvu",
        "newUser": "String", // "Y" or "N"
        "redirectUrl": "Url_to_redirect", example:https://google.co.in
        "fips": ["String"], // Array of FIP IDs
        "institutionType": "String", // e.g., "FIU"
        "entityInfo": {
            "entityId": "String", // Unique entity identifier
            "entityName": "String", // Name of the entity
        },
        "consentFITypes": [
            "String" // Array of FITypes passed in consent request
        ],
        "encryptedres": "String", // Encrypted response string
        "resDate": "String", // Response date in ISO formatß
        "encryptedFiId": "String" // Encrypted financial institution ID
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />


          {/* 2. Login Encrypt */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            2. Login Encrypt
          </Typography>
          <p>Initiates the encrypted OTP-based login process.</p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong> finvuClient.loginEncrypt()
            </Typography>
            <Typography
              sx={{
                fontFamily: "monospace",
                color: "#666",
                mt: 1,
                fontSize: "0.9em",
                whiteSpace: "pre-line",
              }}
            >
              {`Note : No arguments are required for this method.
              In a session only 5 times loginEncrypt() method can be called.`}
            </Typography>
          </Box>

          <h4>Prerequisites</h4>
          <ul>
            <li>
              Must call fiuInfo() first to set up the encrypted request context
            </li>
            <li>WebSocket connection must be established</li>
          </ul>

          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.loginEncrypt()
              `}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />

<h4>Request Schema</h4>
          <CopyBlock
            text={`{
   	"header": {
        "mid": "String",
        "ts": "String",
        "dup": "Boolean",
        "sid": "", 
        "type": "String",
        "csid": "String" 
    },
    "payload": {
        "encryptedRequest": "String", // Encrypted request string
        "requestDate": "String", // Encrypted request date 
        "encryptedFiuId": "String", // Encrypted financial institution ID
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",
        "csid": "String" 
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "authType": "String",
        "authMeta": "Object",
        "maskedId": "String",
        "message": "String",
        "newUser": "String",
        "otpReference": "String",
        "status": "SEND",
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Success Response Example</h4>
          <CopyBlock
            text={`{
    "code": "String", // e.g., "F200" 
    "status": "SEND",
    "message": "OTP sent successfully",
    "maskedId": "XXXXXX9178"
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Error Response Example</h4>
          <CopyBlock
            text={`{
    "code": "String", // e.g., "F200" 
    "status": "FAILURE",
    "message": "Encrypted request context not initialized"
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          {/* 3. OTP Verification */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            3. OTP Verification
          </Typography>
          <p>Verifies the OTP received during the login process.</p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong> finvuClient.verifyOTP(otp)
            </Typography>
            <Typography
              sx={{
                fontFamily: "monospace",
                color: "#666",
                mt: 1,
                fontSize: "0.9em",
                whiteSpace: "pre-line",
              }}
            > 
            {
             `otp: OTP received by user (Required)
              Note : No need to pass otpReference here, it is already handled by the SDK.
              For a single otp 3 attempts are allowed.`
            }
            </Typography>
          </Box>

          <h4>Prerequisites</h4>
          <ul>
            <li>Must have received OTP from loginEncrypt()</li>
            <li>Active session from previous calls</li>
          </ul>

          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.verifyOTP(otp)
              `}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />


          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",
        "csid": "String" 
    },
    "payload": {
        "otpReference": "String", // Reference for the OTP recived in response of loginEncrypt
        "otp": "String" // OTP received by user
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",
        "csid": "String" 
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "SUCCESS",
        "message": "String",
        "sessionId": "String"
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Success Response Example</h4>
          <CopyBlock
            text={`{
    "code": "String", // e.g., "F200" 
    "status": "SUCCESS",
    "message": "OTP verified successfully",
    "sessionId": "abc123xyz"
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Error Response Example</h4>
          <CopyBlock
            text={`{
    "code": "String", // e.g., "F200" 
    "status": "FAILURE",
    "message": "Invalid OTP"
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Error Cases</h4>
          <ul>
            <li>Invalid OTP </li>
            <li>Expired OTP</li>
            <li>Maximum attempts exceeded</li>
            <li>Session timeout</li>
          </ul>

          {/* 4. Get Fip List */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            4. Get FIP List
          </Typography>
          <p>
            Retrieves a list of all Financial Information Providers (FIPs) that are available in the finvu platform.
          </p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong> finvuClient.allFipsList()
            </Typography>
          </Box>
          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.allFipsList()
              `}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />
          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",
        "csid": "String" 
    },
    "payload": {}
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />
          
          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",
        "csid": "String" 
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "SUCCESS",
        "message": null,
        "fipsAll": [    // List of all FIP objects available in the finvu platform
            {
                "fipId": "String",         	// FIP identifier
                "productName": "String",    // FIP name
                "fipFitypes": ["String"],   // List of supported financial types
                "fipFsr": "String",        	// Financial sector regulator
                "productDesc": "String",    // Product description
                "productIconUri": "String", // Icon URL
                "enabled": "Boolean"        // Availability status
            }
        ]
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Success Response Example</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",
        "ts": "String",
        "sid": "String",
        "dup": "Boolean",
        "type": "String",
        "csid": "String"
    },
    "payload": {
        "code": "F200"
        "status": "SUCCESS",
        "message": null,
        "fipsAll": [
            {
                "enabled": true
                "fipId": "IBFIP",
                "fipFitypes": [
                    "DEPOSIT",
                    "RECURRING_DEPOSIT",
                    "TERM-DEPOSIT"
                ],
                "productIconUri": "https://cdn.finvu.in/finvulogos/indian-bank-logo.png",
                "productName": "Indian Bank"
            },
            {
                "enabled": true,
                "fipFitypes": ["DEPOSIT"],
                "fipId": "ICICI-FIP",
                "productIconUri": "https://cdn.finvu.in/finvulogos/icicibank_icon.jpg",
                "productName": "ICICI Bank"
            }
        ]
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Additional Notes</h4>
          <ul>
            <li>
              This endpoint retrieves a list of all Financial Information
              Providers (FIPs)
            </li>
            <li>
              The response includes detailed information about each FIP
              including supported financial types
            </li>
            <li>
              Each FIP entry includes its identifier, name, description, and
              icon URL
            </li>
            <li>
              The enabled field indicates whether the FIP is currently active
              with finvu
            </li>
          </ul>
          {/* 5. Consent Request Details */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            5. Consent Request Details
          </Typography>
          <p>
            Fetches the list of consent requests made by the user, including
            details such as purpose, frequency, and duration for each request consent(template).
          </p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong> finvuClient.consentRequestDetailsEncList()
            </Typography>
          </Box>
          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.consentRequestDetailsEncList()
              `}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />
          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Request type identifier
        "csid": "String"
    },
    "payload": {
        "encryptedRequest": "String", // Encrypted request data
        "requestDate": "String", // Request date in ISO format
        "encryptedFiuId": "String", // Encrypted FIU ID
        "encryptedFiType": null
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",
        "ts": "String",
        "sid": "String",
        "dup": "Boolean",
        "type": "String",   
        "csid": "String"
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "String", // e.g., "SUCCESS" or "FAILURE"
        "message": null,
        "consentRequestList": [
            {
                "status": "String", // e.g., "SUCCESS" or "FAILURE"
                "message": null,
                "consentId": null,
                "ver": "2.0.0",
                "txnid": "String", // Unique transaction ID
                "consentStatus": null,
                "createTime": null,
                "startTime": "String", // ISO format start time
                "expireTime": "String", // ISO format expiration time
                "statusLastupdateTimestamp": null,
                "FIP": null,
                "AA": {
                    "id": "cookiejar-aa@finvu.in"
                },
                "FIU": {
                    "id": "String", // FIU ID
                    "name": "String" // FIU Name
                },
                "User": {
                    "idTypes": "FIU",
                    "id": "String", // User ID
                },
                "Accounts": null,
                "ConsentUse": null,
                "DataAccess": null,
                "Purpose": {
                    "code": "String", // Purpose code
                    "refUri": "https://api.rebit.org.in/aa/purpose/102.xml",
                    "text": "String", // Purpose description
                    "Category": {
                        "type": "Personal Finance"
                    }
                },
                "Signature": null,
                "mode": "STORE",
                "fetchType": "PERIODIC",
                "consentTypes": [
                    "PROFILE",
                    "SUMMARY",
                    "TRANSACTIONS"
                ],
                "consentDisplayDescriptions": [
                    "PROFILE",
                    "SUMMARY",
                    "TRANSACTIONS"
                ],
                "fiTypes": ["String"], // Array of financial types passed in consent request
                "DataDateTimeRange": {
                    "from": "2024-04-10T12:59:22.403+00:00",
                    "to": "2025-04-10T12:59:22.403+00:00"
                },
                "DataLife": {
                    "unit": "MONTH",
                    "value": 1
                },
                "Frequency": {
                    "unit": "MONTH",
                    "value": 44
                },
                "DataFilter": [
                    {
                        "type": "TRANSACTIONAMOUNT",
                        "operator": ">",
                        "value": "0"
                    }
                ],
                "consentDetailDigitalSignature": null,
                "ConsentHandle": "916d3167-c700-4135-9cc3-4e7f6641cd98"
            }
        ]
    }
}
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />
          <h4>Success Response Example</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",
        "ts": "String",
        "sid": "String",
        "dup": "Boolean",
        "type": "String",     // Request type identifier
        "csid": "String"
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "String", // e.g., "SUCCESS" or "FAILURE"
        "message": null,
        "consentRequestList": [
            {
                "status": "String", // e.g., "SUCCESS" or "FAILURE"
                "message": null,
                "consentId": null,
                "ver": "2.0.0",
                "txnid": "String", // Unique transaction ID
                "consentStatus": null,
                "createTime": null,
                "startTime": "String", // ISO format start time
                "expireTime": "String", // ISO format expiration time
                "statusLastupdateTimestamp": null,
                "FIP": null,
                "AA": {
                    "id": "cookiejar-aa@finvu.in"
                },
                "FIU": {
                    "id": "String", // FIU ID
                    "name": "String" // FIU Name
                },
                "User": {
                    "idTypes": "FIU",
                    "id": "String", // User ID
                },
                "Accounts": null,
                "ConsentUse": null,
                "DataAccess": null,
                "Purpose": {
                    "code": "String", // Purpose code
                    "refUri": "https://api.rebit.org.in/aa/purpose/102.xml",
                    "text": "String", // Purpose description
                    "Category": {
                        "type": "Personal Finance"
                    }
                },
                "Signature": null,
                "mode": "STORE",
                "fetchType": "PERIODIC",
                "consentTypes": [
                    "PROFILE",
                    "SUMMARY",
                    "TRANSACTIONS"
                ],
                "consentDisplayDescriptions": [
                    "PROFILE",
                    "SUMMARY",
                    "TRANSACTIONS"
                ],
                "fiTypes": ["String"], // Array of financial types passed in consent request
                "DataDateTimeRange": {
                    "from": "2024-04-10T12:59:22.403+00:00",
                    "to": "2025-04-10T12:59:22.403+00:00"
                },
                "DataLife": {
                    "unit": "MONTH",
                    "value": 1
                },
                "Frequency": {
                    "unit": "MONTH",
                    "value": 44
                },
                "DataFilter": [
                    {
                        "type": "TRANSACTIONAMOUNT",
                        "operator": ">",
                        "value": "0"
                    }
                ],
                "consentDetailDigitalSignature": null,
                "ConsentHandle": "916d3167-c700-4135-9cc3-4e7f6641cd98"
            }
        ]
    }
}
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Error Responses</h4>
          <CopyBlock
            text={`{
    "code": "String", // e.g., "F200" 
    "Status": "FAILURE",
    "message": "Consent not found"
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          {/* 6. User Details Request */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            6. User Details Request
          </Typography>
          <p>Fetches details of the currently logged-in user.</p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong> finvuClient.userDetails()
            </Typography>
          </Box>
          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.userDetails()
              `}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",
        "csid": "String",
    },
    "payload": {
        "userId": "{Mobile Number}@finvu"
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",
        "csid": "String" 
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "String", // e.g., "ACCEPT" or "FAILURE"
        "message": "String", // Additional message
        "UserInfo": {
            "userId": "{Mobile Number}@finvu",
            "mobileNo": "{Mobile Number}",
            "mobileAuthenticated": "String", // "Y" or "N"
            "emailId": "String", // User's email ID
            "emailAuthenticated": "String", // "Y" or "N"
            "panProvided": "String", // "Y" or "N"
            "dobProvided": "String", // "Y" or "N"  
        }
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Success Response Example</h4>
          <CopyBlock
            text={`{
"code": "F200",
"status": "ACCEPT",
"message": "Received UserInfo Successfully",
"UserInfo": {
    "userId": "8076729178@finvu",
    "mobileNo": "8076729178",
    "mobileAuthenticated": "Y",
    "emailId": "",
    "emailAuthenticated": "N"
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          {/* 7. Get Fip List */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            7. User Linked Accounts
          </Typography>
          <p>Fetches the list of accounts linked by user with the Finvu.</p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong> finvuClient.userLinkedAccounts()
            </Typography>
          </Box>
          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.userLinkedAccounts()
`
            }
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />
          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Request type identifier
        "csid": "String"
    },
    "payload": {"String"} // User ID in format "{number}@finvu" (Required) 
    Note : No need to pass userID here, it is already handled by the SDK.
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",
        "ts": "String",
        "sid": "String",
        "dup": "Boolean",
        "type": "String",     
        "csid": "String"
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "SUCCESS",
        "message": null,
        "LinkedAccounts": [
            {
                "userId": "{number}@finvu",
                "fipId": "String", // FIP identifier
                "fipName": "String", // FIP name
                "maskedAccNumber": "String", // Masked account number
                "accRefNumber": "String", // Account reference number
                "linkRefNumber": "String", // Link reference number
                "identifiers": "String", // Account identifiers
                "consentIdList": null,
                "FIType": "String", // Financial type
                "accType": "String", // Account type (e.g., "SAVINGS", "CURRENT")
                "linkedAccountUpdateTimestamp": "String", // Last update timestamp
                "AuthenticatorType": "String", // Authenticator type (e.g., "TOKEN")
            }
        ]
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Success Response Example</h4>
          <CopyBlock
            text={`{
"code": "F200",
"status": "SUCCESS",
"message": null,
"LinkedAccounts": [
        {
            "userId": "finvudemo@finvu",
            "fipId": "BARB0KIMXXX",
            "fipName": "Finvu Bank",
            "maskedAccNumber": "XXXXXXX3054",
            "accType": "CURRENT"
        }
    ]
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          {/* 8. Fip Details */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            8. Fip Details
          </Typography>
          <p>
            Retrieves the list of FI types supported by the FIP and the
            corresponding identifiers required for account discovery.
          </p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong> finvuClient.fipDetails(fipId)
            </Typography>
          </Box>
          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.fipDetails(fipId)
              `}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />
          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Request type identifier
        "csid": "String"
    },
    "payload": {
        "fipId": "fipId" // FIP ID as per central registry (e.g., "dhanagarbank")
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "String", // e.g., "SUCCESS" or "FAILURE"
        "message": null,
        "FipDetails": [
            {
                "fipId": "dhanagarbank",
                "productName": "Dhanagar Bank",
                "fipFiType": [String], // e.g., ["DEPOSIT", "RECURRING_DEPOSIT"]
                "fipFsr": "String", // Financial sector regulator (e.g., "RBI")
                "fipName": "String", // FIP name
                "linkingOtpLength": "Number", // Linking OTP length (e.g. : 6 or 8)
                "productDesc": "String", // Product description
                "productId": null,
                "productSearchKeywords": "String", // Search keywords
                "fipFiTypeIdentifiers": [
                    {
                        "fiType": "DEPOSIT",
                        "identifiers": [
                            {
                                "category": "STRONG",
                                "type": "MOBILE"
                            }
                        ]
                    },
                    {
                        "fiType": "RECURRING_DEPOSIT",
                        "identifiers": [
                            {
                                "category": "STRONG",
                                "type": "MOBILE"
                            }
                        ]
                    },
                    {
                        "fiType": "TERM_DEPOSIT",
                        "identifiers": [
                            {
                                "category": "STRONG",
                                "type": "MOBILE"
                            }
                        ]
                    }
                ]
            }
        ]
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />
          {/* 9. Account Discovery */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            9. Account Discovery
          </Typography>
          <p>Discovers accounts associated with a given FIP.</p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong> finvuClient.discoverAccounts(fipid,
              identifiers)
              <br /> <br />
              <strong>Or</strong>
              <br /> <br />
              <strong>Async Method:</strong> finvuClient.discoverAccountsAsync(fipid,
                identifiers)
            </Typography>
            <Typography
              sx={{
                fontFamily: "monospace",
                color: "#666",
                mt: 1,
                fontSize: "0.9em",
                whiteSpace: 'pre-line',
              }}
            >{
              `
              Parameters: - fipid: FIP ID as per central registry - identifiers:
              List of identifiers (e.g., mobile numbers)
              Note: Request and response is same for both synchronous and asynchronous methods.
              `
            }
            </Typography>
          </Box>
          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.discoverAccounts(fipid, identifiers)

or

finvuClient.discoverAccountsAsync(fipid, identifiers)
              `}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />
          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "ver": "1.1.2",
        "timestamp": "String", // ISO format timestamp
        "txnid": "String", // Unique transaction ID
        "Customer": {
            "id": "{Mobile Number}@finvu",
            "Identifiers": [
                {
                    "category": "String", // e.g., "STRONG" or "WEAK" or "ANCILLARY"
                    "type": "String", // e.g., "MOBILE" or "PAN" or "DOB"
                    "value": "String" // Actual value, e.g., mobile number or PAN or DOB 
                }
            ]
        },
        "FIPDetails": {
            "fipId": "String", // FIP ID as per central registry
            "fipName": "String" // FIP Name
        }
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Fields</h4>
          <ul>
            <li>
              <strong>fipid</strong>: FIP ID as per the central registry.{" "}
              <em>(Required)</em>
            </li>
            <li>
              <strong>identifiers</strong>: List of identifiers such as mobile
              numbers. <em>(Required)</em>
            </li>
          </ul>

          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "String", // e.g., "SUCCESS" or "FAILURE"
        "message": null,
        "ver": "2.0.0",
        "timestamp": "String", // ISO format timestamp
        "txnid": "String", // Unique transaction ID
        "DiscoveredAccounts": [
            {
                "accType": "String", // e.g., "SAVINGS" or "CURRENT"
                "accRefNumber": "String", // Account reference number
                "maskedAccNumber": "String", // Masked account number
                "FIType": "String" // e.g., "DEPOSIT" or "EQUITIES"
            },
            {
                "accType": "String", // e.g., "SAVINGS" or "CURRENT"
                "accRefNumber": "String", // Account reference number
                "maskedAccNumber": "String", // Masked account number
                "FIType": "String" // e.g., "DEPOSIT" or "EQUITIES"
            }
        ]
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Success Response Example</h4>
          <CopyBlock
            text={`{
"code": "String", // e.g., "F200" 
"status": "SUCCESS",
"message": null,
"DiscoveredAccounts": [
        {
            "accType": "SAVINGS",
            "accRefNumber": "BOB352467108",
            "maskedAccNumber": "XXXXXXXXX2108",
            "FIType": "DEPOSIT"
        }
    ]
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Error Responses</h4>
          <CopyBlock
            text={`{
    "code": "String", // e.g., "F200" 
    "Status": "FAILURE",
    "message": "Invalid identifier"
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />
                                      <>
                                <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                                    Difference Between discoverAccounts and discoverAccountsAsync
                                </Typography>

                                <Box sx={{ p: 2 }}>
                                    <Typography variant="subtitle1" sx={{ color: "#0b3452", fontWeight: "bold" }}>
                                        1. discoverAccounts (Synchronous)
                                    </Typography>
                                    <Typography sx={{ mt: 1, mb: 2, color: '#555' }}>
                                        Executes a synchronous request to discover accounts. The backend processes the request and returns the results immediately.
                                    </Typography>
                                    <Box sx={{ pl: 2, borderLeft: '3px solid #0b3452' }}>
                                        <Typography sx={{ color: '#555' }}>
                                            <strong>Use Case:</strong> When you need real-time results and the account discovery process is expected to complete quickly.
                                        </Typography>
                                    </Box>

                                    <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                                        2. discoverAccountsAsync (Asynchronous)
                                    </Typography>
                                    <Typography sx={{ mt: 1, mb: 2, color: '#555' }}>
                                        Executes an asynchronous request to discover accounts. The backend processes the request in the background, and the results may take longer to return.
                                    </Typography>
                                    <Box sx={{ pl: 2, borderLeft: '3px solid #0b3452' }}>
                                        <Typography sx={{ color: '#555' }}>
                                            <strong>Use Case:</strong> When the account discovery process might take more time, or when you can afford to wait for the results without blocking the user interface.
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        mt: 4,
                                        p: 2,
                                        bgcolor: 'rgba(11, 52, 82, 0.05)',
                                        borderRadius: '4px'
                                    }}>
                                        <Typography variant="subtitle1" sx={{ color: "#0b3452", fontWeight: "bold" }}>
                                            Summary
                                        </Typography>
                                        <Box component="ul" sx={{ mt: 1, pl: 3 }}>
                                            <li>
                                                <Typography sx={{ color: '#555' }}>
                                                    Use <code>discoverAccounts</code> for immediate results in scenarios where quick responses are critical.
                                                </Typography>
                                            </li>
                                            <li>
                                                <Typography sx={{ color: '#555' }}>
                                                    Use <code>discoverAccountsAsync</code> for background processing when the discovery process is expected to take longer or can be deferred.
                                                </Typography>
                                            </li>
                                        </Box>
                                    </Box>
                                </Box>
                            </>

          {/* 10. Account Linking */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            10. Account Linking
          </Typography>
          <p>
            Links the user's accounts discovered during the discovery process.
          </p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong> finvuClient.accountLinking(fipid,
              account)
            </Typography>
            <Typography
              sx={{
                fontFamily: "monospace",
                color: "#666",
                mt: 1,
                fontSize: "0.9em",
              }}
            >
              Parameters: - fipid: FIP ID as per central registry - account:
              List of accounts to link
            </Typography>
          </Box>
          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.accountLinking(fipid, account)
              `}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "ver": "1.1.2",
        "timestamp": "String", // ISO format timestamp
        "txnid": "String", // Unique transaction ID
        "FIPDetails": {
            "fipId": "String", // FIP ID as per central registry
            "fipName": ""
        },
        "Customer": {
            "id": "{Mobile Number}@finvu",
            "Accounts": [
                {
                    "accType": "String", // e.g., "SAVINGS" or "CURRENT"
                    "accRefNumber": "String", // Account reference number
                    "maskedAccNumber": "String", // Masked account number
                    "FIType": "String", // e.g., "DEPOSIT" or "EQUITIES"
                }
            ]
        }
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Fields</h4>
          <ul>
            <li>
              <strong>fipid</strong>: FIP ID as per the central registry.{" "}
              <em>(Required)</em>
            </li>
            <li>
              <strong>account</strong>: List of accounts to link. Must include
              fields such as maskedAccNumber, accRefNumber, FIType, and accType.{" "}
              <em>(Required)</em>
            </li>
          </ul>

          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "String", // e.g., "SUCCESS" or "FAILURE"
        "message": null,
        "ver": "2.0.0",
        "timestamp": "String", // ISO format timestamp
        "txnid": "String", // Unique transaction ID
        "AuthenticatorType": "String", // "TOKEN"
        "RefNumber": "String", // Linking reference number
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />
          <h4>Success Response Example</h4>
          <CopyBlock
            text={`{
    "code": "String", // e.g., "F200" 
    "Status": "SUCCESS",
    "message": ""
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Error Responses</h4>
          <CopyBlock
            text={`{
    "code": "String", // e.g., "F200" 
    "Status": "FAILURE",
    "message": "Invalid FIPID"
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          {/* 11. Account Confirm Linking */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            11. Account Confirm Linking
          </Typography>
          <p>
            Confirms the linking of a user's account using the linking reference
            number and OTP.
          </p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong>{" "}
              finvuClient.accountConfirmLinking(accountLinkRefNumber, otp , fipId)
            </Typography>
            <Typography
              sx={{
                fontFamily: "monospace",
                color: "#666",
                mt: 1,
                fontSize: "0.9em",
                whiteSpace: "pre-line",
              }}
            >
              {
                `Parameters:- 
                accountLinkRefNumber: Linking reference number recieved from the accountLinking() method
                otp: OTP received for confirmation
                fipId: It is not required for the confirmation of the linking. 
                It is used for event tracking and analytics purpose if you are using web sdk event tracking.
                `
              }
            </Typography>
          </Box>
          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.accountConfirmLinking(accountLinkRefNumber, otp , fipId)
              `}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />
          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Request type identifier
        "csid": "String"
    },
    "payload": {
        "ver": "1.1.2",
        "timestamp": "String", // ISO format timestamp
        "txnid": "String", // Unique transaction ID
        "AccountsLinkingRefNumber": "String", // Linking reference number
        "token": "String", // OTP received for confirmation
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Fields</h4>
          <ul>
            <li>
              <strong>accountLinkRefNumber</strong>: Linking reference number
              returned in the linking request. <em>(Required)</em>
            </li>
            <li>
              <strong>otp</strong>: OTP sent to the user's mobile number.{" "}
              <em>(Required)</em>
            </li>
            <li>
              <strong>fipId</strong>: It is not required for the confirmation of the linking. 
              It is used for event tracking and analytics purpose if you are using web sdk event tracking.
              <em>(Optional)</em>
            </li>
          </ul>
          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "String", // e.g., "SUCCESS" or "FAILURE"
        "message": null,
        "ver": "2.0.0",
        "timestamp": "String", // ISO format timestamp
        "txnid": "String", // Unique transaction ID
        "AccLinkDetails": [
            {
                "customerAddress": "String", // {mobile number}@finvu
                "linkRefNumber": "String", // Linking reference number
                "accRefNumber": "String", // Account reference number
                "status": "LINKED"
            }
        ]
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          {/* 12. Consent Approval/Denial Request */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            12. Consent Approval/Denial Request
          </Typography>
          <p>Handles the approval or denial of a consent request.</p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong>{" "}
              finvuClient.consentApproveRequest(FIPDetails, handleStatus, consentHandleId)
            </Typography>
            <Typography
              sx={{
                fontFamily: "monospace",
                color: "#666",
                mt: 1,
                fontSize: "0.9em",
                whiteSpace: "pre-line",
              }}
            >
              {`Parameters:- 
              FIPDetails: Array of FIP details objects (Required) , In case of DENY "FIPDetails" should be empty list. 
              e.g : "FIPDetails" : []
              handleStatus: "ACCEPT" | "DENY"
              consentHandleId: It is optional if use case is Single Consent journey in case of multiple consent journey it is required.
              `}
            </Typography>
          </Box>
          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.consentApproveRequest(FIPDetails, handleStatus, consentHandleId)
              `}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />
          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Request type identifier
        "csid": "String"
    },
    "payload": {
        "FIPDetails": [
            {
                "FIP": {
                    "id": "String", // FIP ID
                },
                "Accounts": [
                    {
                        "linkRefNumber": "String", // Linking reference number
                        "accType": "String", // Account type (e.g., "SAVINGS", "CURRENT")
                        "accRefNumber": "String", // Account reference number
                        "maskedAccNumber": "String", // Masked account number
                        "FIType": "String", // Financial type (e.g., "EQUITIES")
                        "fipId": "String", // FIP ID
                        "fipName": "String" // FIP Name
                    }
                ]
            }
        ],
        "FIU": {
            "id": "String", // FIU ID
        },
        "ver": "1.1.2",
        "consentHandleId": "String", // Consent handle ID
        "handleStatus": "String" // "ACCEPT" or "DENY"
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Fields</h4>
          <ul>
            <li>
              <strong>FIPDetails</strong>: Details of the FIP and accounts for
              which consent is being approved or denied. <em>(Required)</em>
            </li>
            <li>
              <strong>handleStatus</strong>: ACCEPT or DENY the consent request.{" "}
              <em>(Required)</em>
            </li>
            <li>
              <strong>Note</strong>: In case of DENY "FIPDetails" should be empty list. e.g : "FIPDetails" : []{" "}
              <em>(Required)</em>
            </li>
          </ul>

          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "fipConsentInfos": [
            {
                "fipId": null,
                "consentId": "String", // Consent ID
            }
        ],
        "consentIntentId": "String", // Consent intent ID
        "status": "String", // e.g., "SUCCESS" or "FAILURE"
        "message": "Consent stored."
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Success Response Example</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "fipConsentInfos": [
            {
                "fipId": null,
                "consentId": "String", // Consent ID
            }
        ],
        "consentIntentId": "String", // Consent intent ID
        "status": "String", // e.g., "SUCCESS" or "FAILURE"
        "message": "Consent stored.",
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Error Responses</h4>
          <CopyBlock
            text={`{
    "code": "String", // e.g., "F200" 
    "Status": "FAILURE",
    "message": "Invalid consent details"
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          {/* 13. Generate Response Parameters */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            13. Generate Response Parameters
          </Typography>
          <p>
            Called at the end of the redirection journey to encrypt the final
            response and provide redirection information back to the FIU
            application.
          </p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong> finvuClient.encryptRequest(errorCode)
            </Typography>
            <Typography
              sx={{
                fontFamily: "monospace",
                color: "#666",
                mt: 1,
                fontSize: "0.9em",
              }}
            >
              Parameters: - errorCode: Status code indicating journey result
            </Typography>
          </Box>
          <h4>Request Example</h4>
          <CopyBlock
            text={`// At the end of the redirection journey
const response = await finvuClient.encryptRequest(0);
if (response.payload.status === "SUCCESS") {
    // 1. Get the encrypted response for the FIU
    const encryptedResult = response.payload.encryptedResponse;
    
    // 2. Get the URL to return to the FIU application
    const returnUrl = response.payload.redirectUrl;
    
    // 3. Redirect back to the FIU application
    window.location.href = returnUrl;
}`}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "errorCode": number, // Status code indicating journey result as per defined by sahmati.
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",
        "csid": "String"
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "SUCCESS",
        "message": null,
        "encryptedResponse": "String", // Encrypted journey result
        "responseDate": "String",      // Response timestamp
        "encryptedFiId": "String",     // Encrypted FI identifier
        "redirectUrl": "String",       // Return URL for FIU application
        "fiuName": "String"           // FIU name for display
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Error Cases</h4>
          <ul>
            <li>Journey status code is missing</li>
            <li>Session has expired</li>
            <li>Invalid journey state</li>
          </ul>

          <h4>Additional Notes</h4>
          <ul>
            <li>
              This API should be called only after completing all necessary
              steps in the redirection journey
            </li>
            <li>
              The encrypted response contains all necessary information about
              the journey's outcome
            </li>
            <li>
              The redirectUrl should be used to return the user to the FIU
              application
            </li>
            <li>
              The journey status is encrypted to maintain security during the
              redirect
            </li>
          </ul>
          {/* 14. Logout */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            14. Logout
          </Typography>
          <p>Logs out the user and closes the WebSocket connection.</p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong> finvuClient.logout()
            </Typography>
          </Box>
          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.logout()
              `}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "userId": "String" // User identifier in the format {mobile number}@finvu
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "code": "String", // e.g., "F200" 
    "Status": "String",
    "message": "String"
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Success Response Example</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "urn:finvu:in:app:res.logout.01",
        "csid": "String"
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "SUCCESS",
        "message": "User {Mobile Number}@finvu logged out."
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Error Responses</h4>
          <CopyBlock
            text={`{
    "code": "String", // e.g., "F200" 
    "Status": "FAILURE",
    "message": "Already logged out"
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          {/* 15. Mobile Verification Request */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            15. Mobile Verification Request
          </Typography>
          <p>
            Used when discovery/linking is to be performed on a mobile number
            other than the registered one.
          </p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong>{" "}
              finvuClient.mobileVerificationRequest(mobileNumber)
            </Typography>
            <Typography
              sx={{
                fontFamily: "monospace",
                color: "#666",
                mt: 1,
                fontSize: "0.9em",
              }}
            >
              Parameters: - mobileNumber: Mobile number to be verified
            </Typography>
          </Box>
          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.mobileVerificationRequest(mobileNumber)
              `}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />
          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "mobileNum": "String" // Mobile number to be verified
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Fields</h4>
          <ul>
            <li>
              <strong>mobileNum</strong>: The mobile number to be verified.{" "}
              <em>(Required)</em>
            </li>
          </ul>

          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "SEND",
        "message": "Otp Sent",
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          {/* 16. Mobile Verification Verify Request */}
          <Typography
            variant="h6"
            sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}
          >
            16. Mobile Verification Verify Request
          </Typography>
          <p>Verifies the OTP sent to the mobile number for verification.</p>

          <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: "monospace", color: "#0b3452" }}>
              <strong>Method:</strong>{" "}
              finvuClient.mobileVerificationVerfiyRequest(mobileNumber, otp)
            </Typography>
            <Typography
              sx={{
                fontFamily: "monospace",
                color: "#666",
                mt: 1,
                fontSize: "0.9em",
              }}
            >
              Parameters: - mobileNumber: Mobile number being verified - otp:
              OTP received on mobile
            </Typography>
          </Box>
          <h4>Request Example</h4>
          <CopyBlock
            text={`
finvuClient.mobileVerificationVerfiyRequest(mobileNumber, otp)
              `}
            language="javascript"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Request Schema</h4>
          <CopyBlock
            text={`{
    "header": {
        "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "mobileNum": "String", // Mobile number being verified
        "otp": "String" // OTP received on mobile
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Response Schema</h4>
          <CopyBlock
            text={`{
    "header": {
       "mid": "String",     // Message ID
        "ts": "String",      // Timestamp
        "sid": "String",     // Session ID
        "dup": "Boolean",    // Duplicate flag
        "type": "String",     // Response type identifier
        "csid": "String"
    },
    "payload": {
        "code": "String", // e.g., "F200" 
        "status": "Status", // e.g., "SUCCESS" or "FAILURE"
        "message": "String", // Message indicating result
    }
}`}
            language="json"
            showLineNumbers={true}
            theme={dracula}
          />

          <h4>Fields</h4>
          <ul>
            <li>
              <strong>mobileNum</strong>: The mobile number to be verified.{" "}
              <em>(Required)</em>
            </li>
            <li>
              <strong>otp</strong>: OTP sent to the user's mobile number.{" "}
              <em>(Required)</em>
            </li>
          </ul>

        </section>

        {/* Rest of the content from SDKIntegration.tsx */}
      </section>
    </Box>
  );

  const EventTrackingContent = () => (
    <Box sx={styles.container}>
      <section style={styles.section}>
        <Typography
          variant="h6"
          sx={{ mt: 0, color: "#0b3452", fontWeight: "bold" }}
        >
          Overview
        </Typography>
        <p>
          The Finvu Event Tracking Library provides standardized event tracking
          capabilities for Finvu's Account Aggregator frontend applications. It
          works seamlessly with the existing Finvu Client SDK without requiring
          any changes to your current implementation.
        </p>
      </section>

      <section style={styles.section}>
        <Typography
          variant="h6"
          sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}
        >
          Getting Started
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Prerequisites
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Platform Integration Setup
        </Typography>

        <section style={styles.section}>
          <Typography
            variant="h6"
            sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}
          >
            Platform Integration Setup
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Paper
              sx={{
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              {/* Platform Selector */}
              <Box
                sx={{
                  borderBottom: "1px solid rgba(0,0,0,0.12)",
                  display: "flex",
                  gap: 1,
                  p: 1,
                }}
              >
                {["React Native", "Android", "iOS", "Iframe"].map(
                  (platform) => (
                    <Typography
                      key={platform.toLowerCase().replace(" ", "-")}
                      onClick={() =>
                        handlePlatformChange(
                          platform.toLowerCase().replace(" ", "-"),
                        )
                      }
                      sx={{
                        px: 3,
                        py: 1.5,
                        cursor: "pointer",
                        borderRadius: "4px",
                        fontSize: "0.95rem",
                        fontWeight:
                          selectedPlatform ===
                          platform.toLowerCase().replace(" ", "-")
                            ? 600
                            : 400,
                        color:
                          selectedPlatform ===
                          platform.toLowerCase().replace(" ", "-")
                            ? "#0b3452"
                            : "#666",
                        bgcolor:
                          selectedPlatform ===
                          platform.toLowerCase().replace(" ", "-")
                            ? "rgba(11, 52, 82, 0.08)"
                            : "transparent",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: "rgba(11, 52, 82, 0.05)",
                          color: "#0b3452",
                        },
                      }}
                    >
                      {platform}
                    </Typography>
                  ),
                )}
              </Box>

              {/* Integration Content */}
              <Box sx={{ p: 2 }}>
                {selectedPlatform === "react-native" && (
                  <>
                    <Typography sx={{ mb: 2 }}>
                      React Native WebView Integration:
                    </Typography>
                    <CopyBlock
                      text={`import { WebView } from 'react-native-webview';

class FinvuWebView extends Component {
    onMessage(e) {
        // Handle tracking events here
        console.log(e.nativeEvent.data);
    }

    render() {
        return (
            <WebView
                source={{ uri: 'YOUR_FINVU_URL' }}
                javaScriptEnabled={true}
                onMessage={this.onMessage.bind(this)}
                domStorageEnabled={true}
            />
        );
    }
}`}
                      language="javascript"
                      showLineNumbers={true}
                      theme={dracula}
                    />
                  </>
                )}
                {selectedPlatform === "android" && (
                  <>
                    <Typography sx={{ mb: 2 }}>
                      Android WebView Integration:
                    </Typography>
                    <CopyBlock
                      text={`class MainActivity : AppCompatActivity() {
    private lateinit var webView: WebView

    override fun onCreate(savedInstanceState: Bundle?) {
        super.onCreate(savedInstanceState)

        webView = WebView(this).apply {
            settings.javaScriptEnabled = true
            addJavascriptInterface(object : Any() {
                @JavascriptInterface
                fun postMessage(eventData: String) {
                    // Process tracking event data
                    Log.d("FinvuTracking", eventData)
                }
            }, "FinvuAndroid")
        }

        webView.loadUrl("YOUR_FINVU_URL")
        setContentView(webView)
    }
}`}
                      language="kotlin"
                      showLineNumbers={true}
                      theme={dracula}
                    />
                  </>
                )}
                {selectedPlatform === "ios" && (
                  <>
                    <Typography sx={{ mb: 2 }}>
                      iOS WKWebView Integration:
                    </Typography>
                    <CopyBlock
                      text={`class ViewController: UIViewController {
    var webView: WKWebView!

    override func viewDidLoad() {
        super.viewDidLoad()

        // Configure WebView
        let config = WKWebViewConfiguration()
        config.preferences.javaScriptEnabled = true
        let contentController = config.userContentController
        contentController.add(self, name: "FinvuIOS")

        webView = WKWebView(frame: view.bounds, configuration: config)
        view.addSubview(webView)

        // Load Finvu URL
        if let url = URL(string: "YOUR_FINVU_URL") {
            webView.load(URLRequest(url: url))
        }
    }
}`}
                      language="swift"
                      showLineNumbers={true}
                      theme={dracula}
                    />
                  </>
                )}
                {selectedPlatform === "iframe" && (
                  <>
                    <Typography sx={{ mb: 2 }}>Iframe Integration:</Typography>
                    <CopyBlock
                      text={`<iframe
    id="finvuFrame"
    src="YOUR_FINVU_URL"
    style="width: 100%; height: 100%; border: none;"
></iframe>
<script>
    window.addEventListener('message', function(event) {
        if (event.origin !== 'https://your-finvu-domain.com') return;
        console.log(event.data);
    }, false);
</script>`}
                      language="html"
                      showLineNumbers={true}
                      theme={dracula}
                    />
                  </>
                )}
              </Box>
            </Paper>
          </Box>
        </section>

        <p>
          All platforms will receive event messages in the following JSON
          format:
        </p>
        <CopyBlock
          text={`{
    "event_name": string,
    "event_category": string,
    "timestamp": string,
    "aa_sdk_version": string,
    // ... additional event-specific parameters
}`}
          language="json"
          showLineNumbers={true}
          theme={dracula}
        />
      </section>

      <section style={styles.section}>
        <Typography
          variant="h6"
          sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}
        >
          Basic Usage
        </Typography>
        <CopyBlock
          text={`// Track any event
window.finvuClient.track('CONSENT_APPROVED', {
    fipCount: '2',
    accountCount: '3',
    fiTypes: ['DEPOSIT', 'RECURRING_DEPOSIT']
});`}
          language="javascript"
          showLineNumbers={true}
          theme={dracula}
        />
      </section>

      <section style={styles.section}>
        <Typography
          variant="h6"
          sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}
        >
          Custom Events and Aliases
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Registering Custom Events
        </Typography>

        <CopyBlock
          text={`// Register custom events
window.finvuClient.registerCustomEvents({
    CUSTOM_LOGIN_SUCCESS: {
        category: 'authentication',
        // other params
    },
    CUSTOM_ERROR: {
        category: 'error',
        // other params
    }
});

// Use custom events
window.finvuClient.track('LINKED_ACCOUNTS_DISCOVERED', {
    count: '2',
});`}
          language="javascript"
          showLineNumbers={true}
          theme={dracula}
        />

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Using Event Aliases
        </Typography>

        <CopyBlock
          text={`// Register aliases for standard events
window.finvuClient.registerAliases({
    'LOGIN_PAGE_LOADED': 'otp_page_loaded',
    'OTP_VERIFIED': 'otp_verification_success'
});

// Use either standard name or alias
window.finvuClient.track('otp_page_loaded', {
    duration: '1000', // in milliseconds
    deviceType: 'mobile'
});`}
          language="javascript"
          showLineNumbers={true}
          theme={dracula}
        />
      </section>

      <section style={styles.section}>
        <Typography
          variant="h6"
          sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}
        >
          Default Parameters
        </Typography>
        <p>Every event tracked automatically includes these parameters:</p>
        <ul>
          <li>event_name: The name of the event or its alias</li>
          <li>event_category: The category the event belongs to</li>
          <li>timestamp: ISO timestamp of when the event occurred</li>
          <li>aa_sdk_version: Version of the AA SDK being used</li>
        </ul>
      </section>

      <section style={styles.section}>
        <Typography
          variant="h6"
          sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}
        >
          Event Categories
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Initialization Events
        </Typography>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Event Name</th>
              <th style={styles.th}>Parameters</th>
              <th style={styles.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>WEBSOCKET_CONNECTED</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>
                WebSocket connection established successfully
              </td>
            </tr>
            <tr>
              <td style={styles.td}>WEBSOCKET_DISCONNECTED</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>WebSocket connection closed or lost</td>
            </tr>
          </tbody>
        </table>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Redirection Events
        </Typography>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Event Name</th>
              <th style={styles.th}>Parameters</th>
              <th style={styles.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>CONSENT_REQUEST_VALID</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>Consent request validation successful</td>
            </tr>
            <tr>
              <td style={styles.td}>CONSENT_REQUEST_INVALID</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>Consent request validation failed</td>
            </tr>
          </tbody>
        </table>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          OTP Events
        </Typography>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Event Name</th>
              <th style={styles.th}>Parameters</th>
              <th style={styles.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>LOGIN_INITIATED</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>Login initiated</td>
            </tr>
            <tr>
              <td style={styles.td}>LOGIN_OTP_GENERATED</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>
                Login OTP successfully generated and sent
              </td>
            </tr>
            <tr>
              <td style={styles.td}>LOGIN_OTP_FAILED</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>Login OTP generation or sending failed</td>
            </tr>
            <tr>
              <td style={styles.td}>LOGIN_OTP_LOCKED</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>
                Account locked after 3 failed OTP attempts
              </td>
            </tr>
            <tr>
              <td style={styles.td}>LOGIN_OTP_VERIFIED</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>Login OTP verification successful</td>
            </tr>
            <tr>
              <td style={styles.td}>LOGIN_OTP_NOT_VERIFIED</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>Login OTP verification failed</td>
            </tr>
          </tbody>
        </table>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Alternate Number Events
        </Typography>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Event Name</th>
              <th style={styles.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>ALTERNATE_NUMBER_ATTEMPTED</td>
              <td style={styles.td}>
                Alternate number verification attempt initiated
              </td>
            </tr>
            <tr>
              <td style={styles.td}>ALTERNATE_NUMBER_OTP_GENERATED</td>
              <td style={styles.td}>
                OTP generated for alternate number verification
              </td>
            </tr>
            <tr>
              <td style={styles.td}>ALTERNATE_NUMBER_OTP_FAILED</td>
              <td style={styles.td}>
                OTP generation failed for alternate number
              </td>
            </tr>
            <tr>
              <td style={styles.td}>ALTERNATE_NUMBER_OTP_VERIFIED</td>
              <td style={styles.td}>
                Alternate number OTP verification successful
              </td>
            </tr>
            <tr>
              <td style={styles.td}>ALTERNATE_NUMBER_OTP_NOT_VERIFIED</td>
              <td style={styles.td}>
                Alternate number OTP verification failed
              </td>
            </tr>
          </tbody>
        </table>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Discovery Events
        </Typography>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Event Name</th>
              <th style={styles.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>ACCOUNTS_DISCOVERED</td>
              <td style={styles.td}>Accounts successfully discovered at FIP</td>
            </tr>
            <tr>
              <td style={styles.td}>DISCOVERY_FAILED</td>
              <td style={styles.td}>Discovery process failed at FIP</td>
            </tr>
            <tr>
              <td style={styles.td}>ACCOUNTS_NOT_DISCOVERED</td>
              <td style={styles.td}>No accounts found or discovery failed</td>
            </tr>
          </tbody>
        </table>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Linking Events
        </Typography>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Event Name</th>
              <th style={styles.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>LINKING_INITIATED</td>
              <td style={styles.td}>Account linking process started</td>
            </tr>
            <tr>
              <td style={styles.td}>LINKING_OTP_GENERATED</td>
              <td style={styles.td}>
                OTP generation initiated for account linking
              </td>
            </tr>
            <tr>
              <td style={styles.td}>LINKING_OTP_FAILED</td>
              <td style={styles.td}>
                OTP generation failed during account linking
              </td>
            </tr>
            <tr>
              <td style={styles.td}>LINKING_SUCCESS</td>
              <td style={styles.td}>Account successfully linked</td>
            </tr>
            <tr>
              <td style={styles.td}>LINKING_FAILURE</td>
              <td style={styles.td}>Account linking failed</td>
            </tr>
          </tbody>
        </table>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Consent Events
        </Typography>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Event Name</th>
              <th style={styles.th}>Parameters</th>
              <th style={styles.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>LINKED_ACCOUNTS_SUMMARY</td>
              <td style={styles.td}>count, fips[], fiTypes[]</td>
              <td style={styles.td}>Summary of accounts in consent approval</td>
            </tr>
            <tr>
              <td style={styles.td}>CONSENT_APPROVED</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>User approved the consent request</td>
            </tr>
            <tr>
              <td style={styles.td}>CONSENT_DENIED</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>User denied the consent request</td>
            </tr>
          </tbody>
        </table>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Error Events
        </Typography>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Event Name</th>
              <th style={styles.th}>Parameters</th>
              <th style={styles.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>SESSION_ERROR</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>
                Session error during WebSocket connection
              </td>
            </tr>
            <tr>
              <td style={styles.td}>SESSION_FAILURE</td>
              <td style={styles.td}>count</td>
              <td style={styles.td}>
                Response failure from server during session
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section style={styles.section}>
        <Typography
          variant="h6"
          sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}
        >
          Best Practices
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Event Names
        </Typography>

        <ul>
          <li>Use predefined event names or configured aliases only</li>
          <li>Event names are case-sensitive</li>
          <li>
            When creating custom events, follow a underscore separated format
          </li>
          <li>Keep custom event names descriptive and specific</li>
        </ul>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Parameters
        </Typography>

        <ul>
          <li>Optional parameters improve event context</li>
          <li>Don't include non-compliant information</li>
          <li>Don't include PII (Personal Identifiable Information)</li>
          <li>For custom events, document the parameters clearly</li>
        </ul>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Error Handling
        </Typography>

        <ul>
          <li>Library handles errors internally</li>
          <li>Check console for warnings/errors during development</li>
          <li>
            Custom events will be validated against their defined configuration
          </li>
        </ul>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Performance
        </Typography>

        <ul>
          <li>Events are sent asynchronously</li>
          <li>No impact on application performance</li>
          <li>Batch custom event registrations when possible</li>
        </ul>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Aliases
        </Typography>

        <ul>
          <li>
            Aliases must be configured through the proper configuration process
          </li>
          <li>Use aliases consistently across your application</li>
          <li>Document aliases in your codebase</li>
          <li>Avoid using the same alias for different events</li>
        </ul>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Custom Events
        </Typography>

        <ul>
          <li>Register all custom events during initialization</li>
          <li>Follow the same category structure as standard events</li>
          <li>Don't duplicate existing standard events</li>
          <li>Keep custom event configurations minimal and focused</li>
        </ul>
      </section>

      <section style={styles.section}>
        <Typography
          variant="h6"
          sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}
        >
          Security Considerations
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          PII Data
        </Typography>

        <ul>
          <li>Never include PII in event parameters</li>
          <li>No PAN, DOB, or account numbers</li>
          <li>Use counts and types instead of specific details</li>
        </ul>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Sensitive Data
        </Typography>

        <ul>
          <li>Be mindful to not track any sensitive customer data</li>
          <li>Don't track session tokens</li>
          <li>Don't track account numbers not given consent for</li>
        </ul>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Pre-Consent Data
        </Typography>

        <ul>
          <li>Don't track specific FIP identifiers before consent</li>
          <li>Don't track account types before consent</li>
          <li>Only track aggregate counts where necessary</li>
          <li>When in doubt, err on the side of privacy</li>
        </ul>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Data Transmission
        </Typography>

        <ul>
          <li>All events are transmitted over secure channels</li>
          <li>Events are validated before transmission</li>
          <li>Failed events are logged but not retried</li>
        </ul>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Access Control
        </Typography>

        <ul>
          <li>Only authorized platforms can receive events</li>
          <li>Origin verification for iframe integration</li>
          <li>Platform-specific message handlers</li>
        </ul>
      </section>

      <section style={styles.section}>
        <Typography
          variant="h6"
          sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}
        >
          Support
        </Typography>
        <ul>
          <li>
            Technical Support:{" "}
            <a href="mailto:support@cookiejar.co.in">support@cookiejar.co.in</a>
          </li>
          <li>
            Documentation:{" "}
            <a href="https://docs.finvu.in">https://docs.finvu.in</a>
          </li>
          <li>SDK Version: 2.7.1</li>
          <li>Tracking Library Version: 1.0.0</li>
        </ul>
      </section>

      <section style={styles.section}>
        <Typography
          variant="h6"
          sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}
        >
          Changelog
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}
        >
          Version 1.0.0
        </Typography>
        <ul>
          <li>Initial release</li>
          <li>Basic event tracking functionality</li>
          <li>Standard event definitions (29 built-in events)</li>
          <li>Custom event registration support</li>
          <li>Event aliasing capabilities</li>
          <li>Cross-platform support (Web, React Native, Android, iOS)</li>
        </ul>
      </section>
    </Box>
  );

  const getNextTabPath = (currentTab: number) => {
    if (currentTab === 0) {
      return "/web-sdk-integration#event-tracking";
    }
    if (currentTab === 1) {
      return "/web-sdk-integration#multiconsent";
    }
    if (currentTab === 2) {
      return "/web-sdk-integration#response-code-mapping";
    }
    return "/mobile-sdk-integration";
  };

  const getPrevTabPath = (currentTab: number) => {
    if (currentTab === 1) {
      return "/web-sdk-integration#sdk";
    }
    if (currentTab === 2) {
      return "/web-sdk-integration#event-tracking";
    }
    if (currentTab === 3) {
      return "/web-sdk-integration#multiconsent";
    }
    return "/api-integration#frontend";
  };

  const TabNavigation = ({ currentTab }: { currentTab: number }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        mt: 6,
        mb: 2,
        borderTop: "1px solid #e0e0e0",
        pt: 4,
      }}
    >
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(getPrevTabPath(currentTab))}
        sx={{ color: "#0b3452" }}
      >
        {currentTab === 1 ? "SDK Integration" : currentTab === 2 ? "Event Tracking" : currentTab === 3 ? "Multi Consent" : "Frontend Integration"}
      </Button>
      <Button
        endIcon={<ArrowForwardIcon />}
        onClick={() => navigate(getNextTabPath(currentTab))}
        sx={{ color: "#0b3452" }}
      >
        {currentTab === 0 ? "Event Tracking" : currentTab === 1 ? "Multi Consent" : currentTab === 2 ? "Response Code Mapping" : "Mobile SDK Integration"}
      </Button>
    </Box>
  );

  return (
    <Box sx={{ paddingLeft: 4 }}>
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: { xs: "1.2rem", sm: "1.5rem" },
          color: "#0b3452",
          display: "inline-block",
          marginBottom: 2,
          lineHeight: 1.5,
        }}
      >
        Web SDK Integration
      </Typography>

      <Box sx={{ mt: 2, mb: 2 }}>
        <img
          src="/images/WebSdkApisFlow.png"
          alt="Finvu Web SDK Flow Diagram"
          style={{
            width: "100%",
            maxWidth: "800px",
            maxHeight: "800px",
            objectFit: "contain",
            height: "auto",
            display: "block",
            margin: "0 auto",
          }}
        />
      </Box>

      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          marginBottom: 3,
        }}
      >
        <Tab label="SDK Integration" />
        <Tab label="Event Tracking" />
        <Tab label="Multi Consent" />
        <Tab label="Response Code Mapping" />
      </Tabs>

      {selectedTab === 0 ? <SDKContent /> : selectedTab === 1 ? <EventTrackingContent /> : selectedTab === 2 ? <MultiConsent /> : (
        <Box sx={styles.container}>
          <Typography
            variant="h6"
            sx={{ mt: 0, color: "#0b3452", fontWeight: "bold" }}
          >
            Backend Error Codes
          </Typography>
          <p>
            These error codes are returned directly from the backend API.
          </p>
          <BackendResponseCodeMapping />
        </Box>
      )}

      <TabNavigation currentTab={selectedTab} />
    </Box>
  );
});

export default WebSDKIntegration;
